import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  X, User, FileText, GraduationCap, CreditCard, Award, Mail, Phone, Calendar, Loader2,
  Home, Briefcase, Compass, Heart, Sparkles, BookOpen, Download, MapPin,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'token';
const authHeaders = () => {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
};

// Labels mappings — kept local to avoid coupling with public form constants.
const LABELS = {
  discovery: {
    site_internet: 'Site internet', reseau_sts: 'Réseau STS', youtube: 'YouTube',
    facebook: 'Facebook', instagram: 'Instagram', linkedin: 'LinkedIn',
    deja_venu: 'Déjà venu·e', recommandation: 'Recommandation', presse: 'Presse', autre: 'Autre',
  },
  situation: {
    gerant: 'Gérant·e société', auto_entrepreneur: 'Auto-entrepreneur', creation: 'En cours de création',
    salarie: 'Salarié·e', interimaire: 'Intérimaire', demandeur_emploi: "Demandeur d'emploi", retraite: 'Retraité·e',
  },
  education: {
    bac3_4: 'Bac+3/Bac+4', bac2: 'Bac+2', bac_btn_bt_bp: 'BAC · BTN · BT · BP',
    bepc_bep_cap: 'BEPC · BEP · CAP', cep_ses: 'CEP · SES',
  },
  formation: {
    decouverte: 'Découverte', generale: 'Générale', nautique: 'Nautique',
    auto: 'Auto', moto: 'Moto', habitat: 'Habitat & médical', dessin: 'Dessin technique',
  },
  handedness: { droitier: 'Droitier·e', gaucher: 'Gaucher·e' },
  sewing: { debutant: 'Débutant·e', confirme: 'Confirmé·e' },
  motivation_tags: {
    renforcer: 'Renforcer mes connaissances',
    nouvelles: 'Acquérir de nouvelles compétences',
    creation: "Projet de création d'entreprise",
    entreprise: 'Formation par mon entreprise',
  },
  particularities: {
    plus_30km: "+ de 30 km d'Istres",
    enfants_moins_10: 'Enfants < 10 ans',
    ras: 'RAS',
  },
  funding: {
    autofinancement: 'Autofinancement', ptp: 'PTP (salarié)', agefiph: 'AGEFIPH',
    region: 'Région', opco: 'OPCO', faf: 'FAF', france_travail: 'France Travail', autre: 'Autre',
  },
};

const labelFor = (group, key) => LABELS[group]?.[key] || key;
const labelList = (group, keys) => (keys || []).map((k) => labelFor(group, k)).join(' · ');

const StatusBadge = ({ status }) => {
  if (!status) return <span className="text-xs text-[#4A5568]">—</span>;
  const map = {
    submitted: 'bg-amber-100 text-amber-700',
    account_created: 'bg-sky-100 text-sky-700',
    dossier_in_progress: 'bg-sky-100 text-sky-700',
    school_review: 'bg-violet-100 text-violet-700',
    funder_review: 'bg-violet-100 text-violet-700',
    payment_pending: 'bg-orange-100 text-orange-700',
    confirmed: 'bg-emerald-100 text-emerald-700',
    rejected: 'bg-rose-100 text-rose-700',
    archived: 'bg-gray-100 text-gray-600',
  };
  const cls = map[status] || 'bg-gray-100 text-gray-700';
  return (
    <span className={`inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-semibold ${cls}`}>
      {status.replace(/_/g, ' ')}
    </span>
  );
};

const Section = ({ title, count, children }) => (
  <section className="border-t border-[#1F2A4A]/8 pt-5 mt-5">
    <div className="flex items-baseline gap-2 mb-3">
      <h3 className="font-serif text-lg text-[#1F2A4A] font-light">{title}</h3>
      {typeof count === 'number' && (
        <span className="text-[10px] font-mono uppercase tracking-wider text-[#C9B896]">{count}</span>
      )}
    </div>
    {children}
  </section>
);

const Field = ({ label, value, Icon }) => {
  if (value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)) return null;
  return (
    <div className="bg-white border border-[#1F2A4A]/8 rounded-lg px-3 py-2.5">
      <p className="text-[9px] font-mono uppercase tracking-[0.18em] text-[#C9B896] mb-1 flex items-center gap-1">
        {Icon && <Icon className="w-2.5 h-2.5" />} {label}
      </p>
      <p className="text-sm text-[#1F2A4A] leading-snug break-words">{value}</p>
    </div>
  );
};

/**
 * Admin-side documents pane — lists the user's CNI / justificatif / RIB and
 * lets the admin download each piece for review or filing.
 */
const ADMIN_DOC_LABELS = {
  id_card: "Carte Nationale d'Identité",
  address_proof: 'Justificatif de domicile',
  rib: 'RIB',
};

const formatBytes = (n) => {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} o`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} Ko`;
  return `${(n / 1024 / 1024).toFixed(1)} Mo`;
};

const AdminDocumentsSection = ({ userId, documents }) => {
  const handleDownload = async (doc) => {
    try {
      const res = await axios.get(
        `${API}/api/admin/users/${userId}/documents/${doc.key}/download`,
        { headers: authHeaders(), responseType: 'blob' }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.file_name || `${doc.key}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      // Silently ignore — admin sees missing docs as "non déposé" rows
      window.alert("Téléchargement impossible : " + (e.response?.data?.detail || e.message));
    }
  };

  const REQUIRED_KEYS = ['id_card', 'address_proof', 'rib'];
  const byKey = Object.fromEntries((documents || []).map((d) => [d.key, d]));
  const rows = REQUIRED_KEYS.map((key) => ({ key, label: ADMIN_DOC_LABELS[key] || key, doc: byKey[key] }));

  return (
    <Section title="Documents administratifs" count={(documents || []).filter((d) => d.file_path).length}>
      <ul className="space-y-2" data-testid="admin-user-documents">
        {rows.map(({ key, label, doc }) => {
          const uploaded = !!(doc && doc.file_path);
          return (
            <li
              key={key}
              className={`rounded-lg px-3 py-2.5 flex items-center justify-between gap-3 border ${
                uploaded ? 'bg-white border-[#C9B896]/40' : 'bg-[#FAF7F0] border-[#1F2A4A]/8'
              }`}
              data-testid={`admin-user-doc-${key}`}
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-[#1F2A4A] truncate">{label}</p>
                {uploaded ? (
                  <p className="text-[10px] text-[#4A5568] mt-0.5 truncate" title={doc.file_name}>
                    {doc.file_name}{doc.file_size ? ` · ${formatBytes(doc.file_size)}` : ''}
                    {doc.uploaded_at ? ` · ${formatDate(doc.uploaded_at)}` : ''}
                  </p>
                ) : (
                  <p className="text-[10px] italic text-[#4A5568] mt-0.5">Non déposé</p>
                )}
              </div>
              {uploaded ? (
                <button
                  onClick={() => handleDownload(doc)}
                  className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-[#1F2A4A] text-[#FAF7F0] hover:bg-[#324166] transition-colors"
                  data-testid={`admin-user-doc-${key}-download`}
                  title={`Télécharger ${label}`}
                >
                  <Download className="w-3 h-3" />
                  Télécharger
                </button>
              ) : (
                <span className="text-[10px] uppercase tracking-wider text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full font-mono shrink-0">
                  Manquant
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </Section>
  );
};

/**
 * Side sheet showing the full recap of a single user — profile, applications,
 * enrollments, payments, points and marketing touchpoints. Slides in from the
 * right edge over the dashboard.
 */
export const UserRecapSheet = ({ userId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!userId) return undefined;
    (async () => {
      if (cancelled) return;
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${API}/api/admin/users/${userId}/recap`, { headers: authHeaders() });
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || 'Impossible de charger la fiche.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  if (!userId) return null;

  const handleDownloadPDF = () => {
    // Opens the print-friendly route in a new tab; the user uses the
    // browser's "Save as PDF" from the print dialog.
    const lang = (window.location.pathname.match(/^\/([a-z]{2})\//) || [])[1] || 'fr';
    window.open(`/${lang}/admin/fiche-eleve/${userId}/print`, '_blank', 'noopener');
  };

  const latestApp = data?.applications?.[0];
  const personal = data?.personal || {};
  const fullAddress = [personal.address_street, personal.address_complement, [personal.address_postcode, personal.address_city].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="fixed inset-0 z-[60] flex justify-end" data-testid="user-recap-sheet">
      <div className="absolute inset-0 bg-[#1F2A4A]/55 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <aside className="relative w-full max-w-[560px] bg-white shadow-2xl h-full overflow-y-auto">
        <header className="sticky top-0 z-10 bg-white border-b border-[#1F2A4A]/8 px-6 py-4 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#C9B896]">Fiche client</p>
            <p className="font-serif text-xl text-[#1F2A4A] font-light leading-tight truncate">
              {data?.user ? `${data.user.first_name || ''} ${data.user.last_name || ''}`.trim() || data.user.email : '…'}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={handleDownloadPDF}
              disabled={!data}
              className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-full bg-[#C9B896] text-[#1F2A4A] hover:bg-[#E0D4BA] transition-colors disabled:opacity-50"
              data-testid="recap-download-pdf"
              title="Télécharger la fiche en PDF"
            >
              <Download className="w-3.5 h-3.5" /> PDF
            </button>
            <button
              onClick={onClose}
              className="w-9 h-9 rounded-full hover:bg-[#FAF7F0] flex items-center justify-center text-[#1F2A4A]"
              data-testid="recap-close"
              title="Fermer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </header>

        <div className="px-6 py-5">
          {loading && (
            <div className="py-20 text-center"><Loader2 className="inline animate-spin text-[#C9B896]" /></div>
          )}
          {error && <div className="py-12 text-center text-rose-600 text-sm">{error}</div>}
          {data && !loading && (
            <>
              {/* Profile */}
              <div className="bg-[#FAF7F0]/60 rounded-2xl p-5 border border-[#C9B896]/30">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-[#1F2A4A] text-[#C9B896] flex items-center justify-center font-serif text-lg">
                    {(data.user.first_name || data.user.email || '?').slice(0, 1).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] uppercase tracking-wider text-[#C9B896]">{data.user.role}</p>
                    <p className="text-sm text-[#1F2A4A] truncate flex items-center gap-1"><Mail className="w-3 h-3" /> {data.user.email}</p>
                    {data.user.phone && <p className="text-xs text-[#4A5568] truncate flex items-center gap-1"><Phone className="w-3 h-3" /> {data.user.phone}</p>}
                  </div>
                </div>
                <p className="text-xs text-[#4A5568] flex items-center gap-1.5"><Calendar className="w-3 h-3" /> Inscrit le {formatDate(data.user.created_at)}</p>
              </div>

              {/* Stats grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-5" data-testid="recap-stats">
                {[
                  { label: 'Candidatures', value: data.stats.applications_count, Icon: FileText },
                  { label: 'Sessions', value: data.stats.enrollments_count, Icon: GraduationCap },
                  { label: 'Payé', value: `${data.stats.payments_total} €`, Icon: CreditCard },
                  { label: 'Points', value: data.stats.points_balance, Icon: Award },
                ].map((s) => (
                  <div key={s.label} className="bg-white border border-[#1F2A4A]/8 rounded-xl p-3">
                    <s.Icon className="w-4 h-4 text-[#C9B896] mb-1.5" />
                    <p className="text-lg font-semibold text-[#1F2A4A] leading-none">{s.value}</p>
                    <p className="text-[10px] uppercase tracking-wider text-[#4A5568] mt-1">{s.label}</p>
                  </div>
                ))}
              </div>

              {/* Personal info from latest application */}
              {latestApp && (
                <Section title="Données personnelles">
                  <p className="text-[10px] text-[#4A5568] mb-3 italic">
                    Issues de la dernière candidature soumise le {formatDate(latestApp.created_at)}.
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <Field label="Âge" value={personal.age} Icon={User} />
                    <Field label="Téléphone" value={data.user.phone} Icon={Phone} />
                    <div className="col-span-2">
                      <Field label="Adresse" value={fullAddress} Icon={MapPin} />
                    </div>
                    <Field label="Particularités" value={labelList('particularities', personal.particularities)} />
                  </div>
                </Section>
              )}

              {/* Découverte + Situation pro */}
              {latestApp && (
                <Section title="Découverte & situation">
                  <div className="grid grid-cols-2 gap-2">
                    <Field label="Canaux découverte" value={labelList('discovery', latestApp.discovery_channels)} Icon={Compass} />
                    <Field label="Recommandation" value={personal.referrer_name} />
                    <Field label="Situation actuelle" value={labelFor('situation', latestApp.current_situation)} Icon={Briefcase} />
                    <Field label="Niveau d'études" value={labelFor('education', personal.education_level)} Icon={BookOpen} />
                    <div className="col-span-2">
                      <Field label="Parcours professionnel" value={personal.professional_history} />
                    </div>
                    <div className="col-span-2">
                      <Field label="Poste actuel" value={personal.current_job_description} />
                    </div>
                    <Field label="ID France Travail" value={personal.france_travail_id} />
                  </div>
                </Section>
              )}

              {/* Formation visée */}
              {latestApp && (
                <Section title="Formation visée">
                  <div className="grid grid-cols-2 gap-2">
                    <Field label="Formation" value={labelFor('formation', personal.target_formation)} Icon={GraduationCap} />
                    <Field label="Période souhaitée" value={personal.preferred_period} Icon={Calendar} />
                    <Field label="Latéralité" value={labelFor('handedness', personal.handedness)} />
                    <Field label="Machine à coudre" value={labelFor('sewing', personal.sewing_machine_level)} />
                    <div className="col-span-2">
                      <Field label="Motivation tags" value={labelList('motivation_tags', personal.motivation_tags)} Icon={Sparkles} />
                    </div>
                    <div className="col-span-2">
                      <Field label="Attentes" value={personal.expectations} />
                    </div>
                    <div className="col-span-2">
                      <Field label="Motivation" value={latestApp.motivation} Icon={Heart} />
                    </div>
                    <div className="col-span-2">
                      <Field label="Expérience" value={latestApp.experience} />
                    </div>
                  </div>
                </Section>
              )}

              {/* Financement & accessibilité */}
              {latestApp && (
                <Section title="Financement & accessibilité">
                  <div className="grid grid-cols-2 gap-2">
                    <Field label="RQTH" value={personal.has_rqth} />
                    <Field label="Source de financement" value={labelFor('funding', latestApp.funding_source)} Icon={CreditCard} />
                    <div className="col-span-2">
                      <Field label="Options financement" value={labelList('funding', latestApp.funding_options)} />
                    </div>
                  </div>
                </Section>
              )}

              {/* Documents administratifs */}
              <AdminDocumentsSection userId={userId} documents={data.user?.documents || []} />

              {/* Applications */}
              <Section title="Candidatures" count={data.applications.length}>
                {data.applications.length === 0 ? (
                  <p className="text-xs text-[#4A5568] italic">Aucune candidature.</p>
                ) : (
                  <ul className="space-y-2">
                    {data.applications.map((a) => (
                      <li key={a.id} className="bg-white border border-[#1F2A4A]/8 rounded-lg px-3 py-2.5 text-sm flex items-start gap-3" data-testid={`recap-app-${a.id}`}>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-[#1F2A4A] truncate">{a.course_title || a.course_id}</p>
                          <p className="text-[10px] text-[#4A5568] mt-0.5">{formatDate(a.created_at)}</p>
                        </div>
                        <StatusBadge status={a.status} />
                      </li>
                    ))}
                  </ul>
                )}
              </Section>

              {/* Enrollments */}
              <Section title="Inscriptions actives" count={data.enrollments.length}>
                {data.enrollments.length === 0 ? (
                  <p className="text-xs text-[#4A5568] italic">Aucune inscription.</p>
                ) : (
                  <ul className="space-y-2">
                    {data.enrollments.map((e) => (
                      <li key={e.id} className="bg-white border border-[#1F2A4A]/8 rounded-lg px-3 py-2.5 text-sm">
                        <p className="font-medium text-[#1F2A4A]">{e.course_title || e.course_id}</p>
                        <p className="text-[10px] text-[#4A5568] mt-0.5">{formatDate(e.created_at)}</p>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>

              {/* Payments */}
              <Section title="Paiements" count={data.payments.length}>
                {data.payments.length === 0 ? (
                  <p className="text-xs text-[#4A5568] italic">Aucun paiement enregistré.</p>
                ) : (
                  <ul className="space-y-2">
                    {data.payments.map((p) => (
                      <li key={p.id} className="bg-white border border-[#1F2A4A]/8 rounded-lg px-3 py-2.5 text-sm flex items-center justify-between">
                        <div>
                          <p className="font-medium text-[#1F2A4A]">{p.amount} € — {p.method || 'inconnu'}</p>
                          <p className="text-[10px] text-[#4A5568]">{formatDate(p.created_at)}</p>
                        </div>
                        <StatusBadge status={p.status} />
                      </li>
                    ))}
                  </ul>
                )}
              </Section>

              {/* Marketing leads */}
              {data.leads.length > 0 && (
                <Section title="Empreintes marketing" count={data.leads.length}>
                  <ul className="space-y-2">
                    {data.leads.map((l) => (
                      <li key={l.id} className="text-xs bg-white border border-[#1F2A4A]/8 rounded-lg px-3 py-2 text-[#4A5568]">
                        {l.kind || 'lead'} — {formatDate(l.created_at)}
                      </li>
                    ))}
                  </ul>
                </Section>
              )}
            </>
          )}
        </div>
      </aside>
    </div>
  );
};

export default UserRecapSheet;
